<template>
  <b-card
    title="Microsoft Configurations"
    tag="article"
    class="mb-2 content-wrapper"
  >
    <!-- View Mode -->
    <transition
      name="fade-slide"
      mode="out-in"
    >
      <div
        v-if="isLoading || isUpdatingMicrosoftEnabled"
        key="loader"
        class="text-center py-5"
      >
        <b-spinner
          variant="primary"
          label="Loading..."
        />
      </div>
      <div
        v-else
        key="content"
      >
        <b-form-group>
          <div class="d-flex align-items-center">
            <b-form-radio-group
              v-model="microsoftEnabled"
              name="microsoft-config-radios"
              :disabled="isUpdatingMicrosoftEnabled"
            >
              <b-form-radio :value="0">
                Disabled
              </b-form-radio>
              <b-form-radio :value="1">
                Enabled
              </b-form-radio>
            </b-form-radio-group>
          </div>
        </b-form-group>

        <div v-if="microsoftEnabled == 1">
          <div
            v-if="microsoftConfigMode === 'view' && microsoftConfigurations"
            key="view"
          >
            <div class="d-flex justify-content-end mb-3">
              <b-button
                variant="outline-primary"
                size="sm"
                class="btn btn-primary rounded d-flex align-items-center gap-2 py-75 px-1 rounded-pill"
                @click="microsoftConfigMode = 'edit'"
              >
                Edit Configurations
              </b-button>
            </div>

            <div class="info-grid">
              <div class="info-item">
                <span class="info-label">Client ID</span>
                <span class="info-value">{{
                  microsoftConfigurations.client_id
                }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Client Secret</span>
                <span class="info-value masked">
                  {{ microsoftConfigurations.client_secret }}
                </span>
              </div>
              <div class="info-item">
                <span class="info-label">Tenant ID</span>
                <span class="info-value">{{
                  microsoftConfigurations.tenant_id
                }}</span>
              </div>
              <div class="info-item">
                <span class="info-label">Redirect URL</span>
                <span class="info-value">{{
                  microsoftConfigurations.redirect_url
                }}</span>
              </div>
            </div>
          </div>

          <!-- Edit Mode -->
          <div
            v-else
            key="edit"
          >
            <validation-observer ref="microsoftConfigObserver">
              <b-form
                class="ai-config-form"
                autocomplete="off"
                @submit.prevent="submitMicrosoftForm"
              >
                <b-row>
                  <b-col md="6">
                    <b-form-group
                      label="Client ID"
                      label-for="clientId"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Client ID"
                        rules="required"
                      >
                        <b-form-input
                          id="clientId"
                          v-model="MicrosoftForm.client_id"
                          name="clientId"
                          placeholder="Enter Client ID"
                        />
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="Client Secret"
                      label-for="clientSecret"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Client Secret"
                        rules="required"
                      >
                        <b-form-input
                          id="clientSecret"
                          v-model="MicrosoftForm.client_secret"
                          name="clientSecret"
                          placeholder="Enter Client Secret"
                          type="text"
                        />
                        <small
                          class="form-hint"
                        >Your secure Client Secret</small>
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="Tenant ID"
                      label-for="tenantId"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Tenant ID"
                        rules="required"
                      >
                        <b-form-input
                          id="tenantId"
                          v-model="MicrosoftForm.tenant_id"
                          name="tenantId"
                          placeholder="Enter Tenant ID"
                        />
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group
                      label="Redirect URL"
                      label-for="redirectUrl"
                    >
                      <validation-provider
                        #default="{ errors }"
                        name="Redirect URL"
                        rules="required"
                      >
                        <b-form-input
                          id="redirectUrl"
                          v-model="MicrosoftForm.redirect_url"
                          name="redirectUrl"
                          placeholder="Enter Redirect URL"
                        />
                        <span class="text-danger">{{ errors[0] }}</span>
                      </validation-provider>
                    </b-form-group>
                  </b-col>
                </b-row>
                <div class="form-actions d-flex justify-content-end gap-2">
                  <b-button
                    v-if="
                      microsoftConfigurations && microsoftConfigurations.id
                    "
                    variant="secondary"
                    class="mr-1 rounded-pill"
                    :disabled="isSubmittingMicrosoft"
                    @click="cancelEditMicrosoft"
                  >
                    Cancel
                  </b-button>
                  <b-button
                    type="submit"
                    variant="primary"
                    class="submit-btn rounded-pill"
                    :disabled="isSubmittingMicrosoft"
                  >
                    <span
                      v-if="!isSubmittingMicrosoft"
                    >Save Microsoft Configurations</span>
                    <b-spinner
                      v-else
                      small
                    />
                  </b-button>
                </div>
              </b-form>
            </validation-observer>
          </div>
        </div>
      </div>
    </transition>
  </b-card>
</template>

<script>
import { mapGetters } from 'vuex'
import { ValidationObserver, ValidationProvider } from 'vee-validate'
import {
  BRow,
  BCol,
  BCard,
  BButton,
  BSpinner,
  BForm,
  BFormGroup,
  BFormInput,
  BFormRadio,
  BFormRadioGroup,
} from 'bootstrap-vue'

export default {
  name: 'InstitutionMicrosoftConfiguration',
  components: {
    BRow,
    BCol,
    BCard,
    BButton,
    BSpinner,
    BForm,
    BFormGroup,
    BFormInput,
    BFormRadio,
    BFormRadioGroup,
    ValidationObserver,
    ValidationProvider,
  },
  props: {
    institutionId: {
      required: true,
    },
    institution: {
      type: Object,
      required: false,
      default: () => ({}),
    },
  },
  computed: {
    ...mapGetters({
      microsoftConfigurations: 'institutions/microsoftConfigurations',
    }),
    microsoftEnabled: {
      get() {
        // Ensure default is 0 (Disabled) if value is missing or falsey
        return this.institution && this.institution.microsoft_enabled
          ? Number(this.institution.microsoft_enabled)
          : 0
      },
      set(value) {
        this.updateMicrosoftEnabled(value)
      },
    },
  },

  data() {
    return {
      microsoftFormMode: 'add',
      isSubmittingMicrosoft: false,
      MicrosoftForm: {
        client_id: '',
        client_secret: '',
        tenant_id: '',
        redirect_url: '',
      },
      microsoftConfigMode: 'view',
      isLoading: false,
      isUpdatingMicrosoftEnabled: false,
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    init() {
      this.isLoading = true
      this.$store
        .dispatch('institutions/getMicrosoftConfigurations', this.institutionId)
        .then(() => {
          if (
            this.microsoftConfigurations
            && this.microsoftConfigurations.id
          ) {
            this.microsoftFormMode = 'edit'
            this.microsoftConfigMode = 'view'
            this.populateMicrosoftForm()
          } else {
            this.microsoftFormMode = 'add'
            this.microsoftConfigMode = 'edit'
            this.MicrosoftForm = {
              client_id: '',
              client_secret: '',
              tenant_id: '',
              redirect_url: '',
            }
          }
        })
        .finally(() => {
          this.isLoading = false
        })
    },
    updateMicrosoftEnabled(value) {
      if (value === 1) {
        if (this.microsoftConfigurations && this.microsoftConfigurations.id) {
          this.microsoftConfigMode = 'view'
        } else {
          this.microsoftConfigMode = 'edit'
        }
      }
      this.isUpdatingMicrosoftEnabled = true
      this.$store
        .dispatch('institutions/updateMicrosoftEnabled', {
          id: this.institutionId,
          query: { microsoft_enabled: value },
        })
        .then(() => {
          this.isUpdatingMicrosoftEnabled = false
        })
        .catch(error => {
          this.isUpdatingMicrosoftEnabled = false
          let errorMsg = 'An error occurred.'
          if (error?.response?.data?.errors) {
            errorMsg = Object.entries(error.response.data.errors)
              .map(([field, messages]) => `${field}: ${messages.join(', ')}`)
              .join('\n')
          } else if (error?.response?.data?.message) {
            errorMsg = error.response.data.message
          }
          this.$swal({
            title: 'Error!',
            text: errorMsg,
            icon: 'error',
            confirmButtonText: 'OK',
          })
        })
    },
    populateMicrosoftForm() {
      this.MicrosoftForm = {
        client_id: this.microsoftConfigurations.client_id || '',
        client_secret: this.microsoftConfigurations.client_secret || '',
        tenant_id: this.microsoftConfigurations.tenant_id || '',
        redirect_url: this.microsoftConfigurations.redirect_url || '',
        institution_id: this.institutionId,
        id: this.microsoftConfigurations.id,
      }
    },
    cancelEditMicrosoft() {
      this.microsoftConfigMode = 'view'
      this.populateMicrosoftForm()
    },
    submitMicrosoftForm() {
      this.$refs.microsoftConfigObserver.validate().then(success => {
        if (success) {
          this.isSubmittingMicrosoft = true
          // Ensure institution_id is in the payload
          this.MicrosoftForm.institution_id = this.institutionId

          if (this.microsoftFormMode === 'edit') {
            this.$store
              .dispatch(
                'institutions/updateMicrosoftConfigurations',
                { id: this.MicrosoftForm.id, query: this.MicrosoftForm },
              )
              .then(() => {
                this.isSubmittingMicrosoft = false
                this.$swal({
                  title: 'Success!',
                  text: 'Microsoft configurations updated successfully.',
                  icon: 'success',
                  confirmButtonText: 'OK',
                })
                this.init()
              })
              .catch(error => {
                this.handleError(error)
                this.isSubmittingMicrosoft = false
              })
          } else {
            this.$store
              .dispatch(
                'institutions/storeMicrosoftConfigurations',
                this.MicrosoftForm,
              )
              .then(() => {
                this.isSubmittingMicrosoft = false
                this.$swal({
                  title: 'Success!',
                  text: 'Microsoft configurations saved successfully.',
                  icon: 'success',
                  confirmButtonText: 'OK',
                })
                this.init()
              })
              .catch(error => {
                this.handleError(error)
                this.isSubmittingMicrosoft = false
              })
          }
        } else {
          this.$swal({
            icon: 'error',
            title: 'Validation Error',
            text: 'Please fill in all required fields.',
          })
        }
      })
    },
    handleError(error) {
      let errorMsg = 'An error occurred.'
      if (error?.response?.data?.errors) {
        errorMsg = Object.entries(error.response.data.errors)
          .map(
            ([field, messages]) => `${field}: ${messages.join(', ')}`,
          )
          .join('\n')
      } else if (error?.response?.data?.message) {
        errorMsg = error.response.data.message
      }
      this.$swal({
        title: 'Error!',
        text: errorMsg,
        icon: 'error',
        confirmButtonText: 'OK',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.content-wrapper {
  background: #000a1e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  backdrop-filter: blur(10px);
  color: #e9ecef;
  animation: slideInLeft 0.5s ease forwards;

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-30px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(480px, 1fr));
  gap: 1.5rem;
  margin-top: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

.info-item {
  background: rgba(255, 255, 255, 0.05);
  padding: 1rem;
  border-radius: 10px;
  border-left: 4px solid rgba(15, 217, 222, 0.5);
  transition: transform 0.3s ease, border-color 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    border-left-color: rgba(133, 13, 204, 1);
    background: rgba(255, 255, 255, 0.08);
  }
}

.info-label {
  display: block;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-value {
  display: block;
  font-size: 1.1rem;
  color: #fff;
  font-weight: 500;
  word-break: break-word;
}

.masked {
  font-family: "Courier New", monospace;
  background: rgba(0, 0, 0, 0.2);
  padding: 2px 8px;
  border-radius: 4px;
  display: inline-block;
}

.ai-config-form {
  .modern-input {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.2) !important;
    color: #fff !important;
    border-radius: 8px;
    padding: 0.75rem 1rem;
    transition: all 0.3s ease;

    &:focus {
      background: rgba(255, 255, 255, 0.08) !important;
      border-color: rgba(15, 217, 222, 0.5) !important;
      box-shadow: 0 0 0 3px rgba(15, 217, 222, 0.1) !important;
    }

    &::placeholder {
      color: rgba(255, 255, 255, 0.4) !important;
    }
  }

  .form-hint {
    display: block;
    margin-top: 0.5rem;
    color: rgba(255, 255, 255, 0.6);
    font-size: 0.85rem;
  }
}

.form-actions {
  display: flex;
  gap: 1rem;
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    flex-direction: column;
  }
}

.submit-btn {
  background: linear-gradient(
    90deg,
    rgba(133, 13, 204, 1) 0%,
    rgba(15, 217, 222, 1) 100%
  ) !important;
  border: none;
  padding: 0.75rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(133, 13, 204, 0.4);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}
</style>
